import type Trip from '@/classes/trip_classes/trip'
import type Vehicle from '@/classes/vehicle_classes/vehicle'

interface RecommendVehiclesReturn {
  message?: string
  higherRangeVehicles: Vehicle[]
  lowerRangeVehicles: Vehicle[]
}

/**
 * Returns two lists of vehicles and an optional message.
 * The first list (higherRangeVehicles) contains vehicles with a max range greater than or equal to the trip length
 * The second list (lowerRangeVehicles) contains vehicles with a max range less than the trip length
 * All lists are capped at 10
 * If no vehicles can be found that are rated to carry the maximum departure load weight across all steps, a message is returned
 * Lists are ordered by range closest to trip length
 * @param vehicles array of vehicles to recommend from
 * @param trip trip object to recommend vehicles for
 * @returns an object with two arrays of vehicles and an optional message
 */
export function recommendVehicles({
  vehicles,
  trip,
}: {
  vehicles: Vehicle[]
  trip: Trip
}): RecommendVehiclesReturn {
  // notes form Richard

  // 1. User has progressed to recommendation page and has a trip with Energy, km and payload (note, the max payload should be calculated across all stops, I.e. trip might start with 1000kg but it then might remove 250kg and then add 500 at a stop, making its max weight at some point on the trip 1,250 kg)
  // 2. Filter vehicles by payload weight and proceed to step 3 with the subset of filtered vehicles.
  // 2b. If all vehicles filtered out add message “We can’t find any vehicles that are rated to carry _____ kilograms” and then proceed to Step 3 with all vehicles.
  // 3. Create two ranked lists, list A and list B. List A is where vehicles’ Max Range is greater than the (trip length 1.2 ) and list B is where vehicle’s Max Range is less than the (trip length 1.2).
  // 3.a Rank vehicles in List A from lowest Max Range to highest.
  // 3.b Rank vehicles in List B from highest Max Range to lowest.
  // 3.c IF Max Range is the same, on 2 vehicles, order them based on the lowest weight, then the lowest drag coefficient.

  // below effects rendering not logic, to be handled in UI not here.

  // 4. Append List B onto the bottom of List A (all vehicles where trip can be done without charging or refuelling with hydrogen are ordered first).
  // 5. Select the 10 top vehicles in the list (or however many Ruth’s design allows for) and do an Energy call along the route.
  // 6. Display the energy in kWh or kg of hydrogen with each recommendation.


  // ------ code bases on notes above ------

  // vehicles will need an ev model, max range and max payload
  const filteredVehicles = vehicles.filter((vehicle) => {
    return vehicle.evModel && vehicle.evModel.maxRange // todo: add max payload when we have it.
  })

  // calculate trip length
  const tripInflatedLength = ((trip.itinerary.totalDrivingDistance ?? 0) * 1.2) / 1000 // default to 0 in case where trip data is invalid.

  // calculate max payload for trip: find the maximum departure load weight across all steps
  const maxPayload = Math.max(...trip.itinerary.steps.map((obj) => obj.departureLoadWeight ?? 0))

  // filter vehicles by payload weight
  const filteredByPayload = filteredVehicles.filter((vehicle) => {
    return true // vehicle.evModel?.maxPayload >= maxPayload // todo: add max payload when we have it
  })

  // cannot find any vehicles that are rated to carry maxPayload
  const cannotFindVehiclesToCarryMaxPayload = filteredByPayload.length === 0

  // Determine which list of vehicles to use for splitting
  const vehiclesToSplit = cannotFindVehiclesToCarryMaxPayload ? filteredVehicles : filteredByPayload

  // split vehicles into two groups: vehicles with max range greater than or equal to trip length and vehicles with max range less than trip length
  const unsortedHigherRangeVehicles = vehiclesToSplit.filter((vehicle) => {
    return (vehicle.evModel?.maxRange ?? 0) >= tripInflatedLength
  })
  const unsortedLowerRangeVehicles = vehiclesToSplit.filter((vehicle) => {
    return (vehicle.evModel?.maxRange ?? 0) < tripInflatedLength
  })

  // sort vehicles by max range closest to trip length
  const sortedHigherRangeVehicles = unsortedHigherRangeVehicles.sort((a, b) => {
    if (a.evModel?.maxRange === b.evModel?.maxRange) {
      if (a.mass === b.mass) {
        return a.dragCoefficient - b.dragCoefficient // second tie breaker prioritising aerodynamic vehicles
      } else {
        return a.mass - b.mass // first tie breaker prioritising lighter vehicles
      }
    } else {
      return (a.evModel?.maxRange ?? 0) - (b.evModel?.maxRange ?? 0)
    }
  })

  const sortedLowerRangeVehicles = unsortedLowerRangeVehicles.sort((a, b) => {
    if (a.evModel?.maxRange === b.evModel?.maxRange) {
      if (a.mass === b.mass) {
        return a.dragCoefficient - b.dragCoefficient // second tie breaker prioritising aerodynamic vehicles
      } else {
        return a.mass - b.mass // first tie breaker prioritising lighter vehicles
      }
    } else {
      return (b.evModel?.maxRange ?? 0) - (a.evModel?.maxRange ?? 0)
    }
  })

  // Cap List A at 10 vehicles
  const cappedHigherRangeVehicles = sortedHigherRangeVehicles.slice(0, 10)

  // Cap List B at 10 vehicles
  const cappedLowerRangeVehicles = sortedLowerRangeVehicles.slice(0, 10)

  // compile return object
  const returnObject = {
    message: cannotFindVehiclesToCarryMaxPayload
      ? `We can’t find any vehicles that are rated to carry ${maxPayload} kilograms`
      : undefined,
    higherRangeVehicles: cappedHigherRangeVehicles,
    lowerRangeVehicles: cappedLowerRangeVehicles,
  }

  // return return object
  return returnObject
}

/**
 * Given a trip, return 5 best recommended vehicles in order of ranking.
 *
 * @param trip The trip to find recommended vehicles for.
 * @returns An array of 5 recommended vehicles in order of ranking.
 */
export function getRecommendedVehiclesFromTripComparisons(trip: Trip): Vehicle[] {
  // separate the recommendations from the rest of the comparisons
  const recommendedComparisons = trip.comparisons.filter((c) => c.isRecommendation)
  // filter out comparisons without a valid directusId
  const filteredComparisons = recommendedComparisons.filter((c) => c.vehicle.directusId !== undefined)  
  // return 5 best recommended vehicles in order of ranking
  return filteredComparisons.map((c) => c.vehicle).slice(0, 5)
}
